import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {Check, ChevronRight, CircleDot, RefreshCw} from 'lucide-react';
import {StatusChip, type StatusTone} from '../components/StatusChip';
import {Button, Card, CardHead, INK} from '../onboarding/review/reviewStyles';
import {DecisionBar, Journey, Layout, Note, SmallButton, TaskButton, TaskRail, Work} from '../onboarding/review/contractReviewStyles';
import type {AttentionItem, ProgramAction, ProgramOverview, ProgramWorkspace} from './forwardFlowOverviewApi';
import {count, day, money} from './forwardFlowWords';
import {Grid, Scroller} from './ForwardFlowShared';

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.55; margin-top: 14px;
  h2, h3, p { margin: 0; } h2 { font-size: 17px; } h3 { font-size: 15px; }
`;
const Selected = styled(Card)`
  border-color: #43617f; margin-bottom: 14px;
  .head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; }
  .meta { color: ${INK.muted}; font-size: 13px; margin-top: 6px; }
`;
const Walk = styled(Grid)`
  td.source { color: ${INK.muted}; font-size: 12px; }
`;

const TONE: Record<string, StatusTone> = {critical: 'critical', warning: 'warning', progress: 'progress'};
const TONE_WORD: Record<string, string> = {critical: 'Urgent', warning: 'Needs attention', progress: 'In progress'};

/**
 * The program at a glance, on the review standard: where it is in its life, what needs
 * attention worst first, and the single next action. Every figure is the server's.
 */
export function ForwardFlowOverviewWorkspace({overview, loading, onRefresh, onOpen}: {
  overview: ProgramOverview; loading: boolean; onRefresh: () => void;
  onOpen: (workspace: ProgramWorkspace, query?: string | null) => void;
}) {
  const {facts, journey, attention, offers, nextAction, closure} = overview;
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = useMemo<AttentionItem | null>(() => attention.find(i => i.key === selectedKey) ?? null, [attention, selectedKey]);
  const openOffers = offers.filter(o => o.open);
  const runoff = overview.status === 'EXPIRED' || overview.status === 'TERMINATED';
  const act = (a: ProgramAction) => onOpen(a.workspace, a.query);

  return <Shell>
    <Journey aria-label="Program progress" style={{gridTemplateColumns: `repeat(${journey.length}, 1fr)`}}>
      {journey.map((step, index) => <li key={step.key} data-current={step.current}>
        <span className={`step-icon ${step.done ? 'done' : ''}`}>{step.done ? <Check size={15} /> : <CircleDot size={15} />}</span>
        <div><strong>{index + 1}. {step.label}</strong><small>{step.detail}</small></div>
      </li>)}
    </Journey>

    {!overview.purchaseGate.open && overview.purchaseGate.reason && <Note style={{marginBottom: 16}}>
      <strong>New purchases are on hold.</strong> {overview.purchaseGate.reason}</Note>}

    <Layout>
      <TaskRail aria-label="What needs attention">
        <div className="rail-heading"><h2>What needs attention</h2>
          <p>{attention.length ? `${count(attention.length, 'item')} across the agreement, offers, servicing, claims and controls, worst first.`
            : 'Nothing is open across the agreement, offers, servicing, claims and controls.'}</p></div>
        <div className="tasks">
          {attention.map(item => <TaskButton key={item.key} type="button" $active={selected?.key === item.key}
              onClick={() => setSelectedKey(item.key === selectedKey ? null : item.key)}>
            <span className="category">{item.category}</span>
            <strong>{item.text}</strong>
            <span className="reason"><StatusChip tone={TONE[item.tone] ?? 'neutral'} label={TONE_WORD[item.tone] ?? 'Open'} />
              {item.dueDate ? `due ${day(item.dueDate)}` : ''}</span>
          </TaskButton>)}
          {attention.length === 0 && <div className="empty">Nothing needs attention.</div>}
        </div>
      </TaskRail>

      <Work aria-label="Program overview">
        <div className="work-heading">
          <div><h2>{overview.programName}</h2><p>{overview.programCode} · {overview.portfolioName} · {overview.statusLabel}</p></div>
          <SmallButton type="button" onClick={onRefresh} disabled={loading}><RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}</SmallButton>
        </div>

        {selected && <Selected>
          <div className="head">
            <div><StatusChip tone={TONE[selected.tone] ?? 'neutral'} label={TONE_WORD[selected.tone] ?? 'Open'} size="md" />
              <strong style={{marginLeft: 8}}>{selected.text}</strong></div>
            <Button $variant="primary" type="button" disabled={!selected.nextAction.actorMayAct}
              onClick={() => act(selected.nextAction)}>{selected.nextAction.label} <ChevronRight size={14} /></Button>
          </div>
          <p className="meta">{selected.category} · {selected.detail}{selected.nextAction.whyNot ? ` · ${selected.nextAction.whyNot}` : ''}</p>
        </Selected>}

        <Card style={{marginBottom: 14}}>
          <CardHead><div><h2>Offers in progress</h2>
            <p>{openOffers.length ? 'Each offer moves from receipt to a paid purchase. Open one to take its next step.'
              : runoff ? 'The program is in runoff and takes no new offers.' : 'No offer is in progress.'}</p></div>
            {!runoff && overview.status === 'ACTIVE' && <Button type="button" onClick={() => onOpen('offers', 'offer=new')}>Receive an offer <ChevronRight size={14} /></Button>}
          </CardHead>
          {openOffers.length > 0 && <Scroller><Grid>
            <thead><tr><th>Seller reference</th><th>Stage</th><th className="num">Loans</th><th className="num">Balance</th><th>Next step</th><th></th></tr></thead>
            <tbody>{openOffers.map(o => <tr key={o.deliveryId}>
              <td><button className="link" onClick={() => onOpen('offers', `offer=${o.deliveryId}`)}>{o.sellerReference}</button>
                <div className="muted" style={{fontSize: 12, color: INK.muted}}>cutoff {day(o.cutoffDate)}</div></td>
              <td>{o.stage}</td>
              <td className="num">{(o.proposedCount ?? o.normalizedCount ?? 0).toLocaleString('en-US')}</td>
              <td className="num">{money(o.proposedUpb ?? o.normalizedUpb, 0)}</td>
              <td>{o.nextAction.label}{o.nextAction.whyNot && <div style={{fontSize: 12, color: '#edcc82'}}>{o.nextAction.whyNot}</div>}</td>
              <td><SmallButton type="button" onClick={() => act(o.nextAction)}>Open</SmallButton></td>
            </tr>)}</tbody>
          </Grid></Scroller>}
        </Card>

        <Card style={{marginBottom: 14}}>
          <CardHead><div><h2>Purchase capacity</h2>
            <p>{facts.capacity.known ? `From the agreement's limits and the verified opening capacity of ${day(facts.capacity.asOf)}.`
              : facts.capacity.reason}</p></div>
            {!facts.capacity.known && !facts.capacity.closed && facts.agreement.versionNumber != null &&
              <Button type="button" onClick={() => onOpen('controls', 'authority=baseline')}>Open Controls <ChevronRight size={14} /></Button>}
          </CardHead>
          {facts.capacity.known && <Walk>
            <tbody>
              <tr><td>Total purchase limit</td><td className="num">{money(facts.capacity.limit, 0)}</td><td className="source">agreement version {facts.agreement.versionNumber}</td></tr>
              <tr><td>Purchased to date</td><td className="num">− {money(facts.capacity.purchased, 0)}</td><td className="source">official book and opening capacity</td></tr>
              <tr><td>Approved, not yet booked</td><td className="num">− {money(facts.capacity.committed, 0)}</td><td className="source">approved purchases</td></tr>
            </tbody>
            <tfoot>
              <tr><td>Remaining</td><td className="num">{money(facts.capacity.remaining, 0)}</td><td className="source">computed</td></tr>
              {facts.capacity.monthlyLimit != null && <tr><td>Remaining this month</td><td className="num">{money(facts.capacity.monthlyRemaining, 0)}</td>
                <td className="source">of {money(facts.capacity.monthlyLimit, 0)} a month</td></tr>}
            </tfoot>
          </Walk>}
        </Card>

        <Card style={{marginBottom: 14}}>
          <CardHead><div><h2>Servicing</h2>
            <p>{facts.servicing.periodLabel ? `${facts.servicing.periodLabel} · ${facts.servicing.nextStep}`
              : Number(facts.owned.loans) > 0 ? 'No servicing period needs work today.' : 'Servicing starts in the month the first purchase is booked.'}</p></div>
            {facts.servicing.periodId != null && <Button type="button" onClick={() => onOpen('collections', `period=${facts.servicing.periodId}`)}>
              Open collections <ChevronRight size={14} /></Button>}
          </CardHead>
          <Grid><tbody>
            <tr><td>Loans owned</td><td className="num">{count(Number(facts.owned.loans), 'loan')} · {money(facts.owned.upb, 0)}</td></tr>
            <tr><td>Owed to the seller for booked loans</td><td className="num">{money(facts.owedToSeller.payable)}</td></tr>
            {Number(facts.owedToSeller.holdback) > 0 && <tr><td>Holdback not yet due</td><td className="num">{money(facts.owedToSeller.holdback)}</td></tr>}
            <tr><td>Differences to explain this period</td><td className="num">{facts.servicing.openItems ?? 0}</td></tr>
            <tr><td>Open seller claims</td><td className="num">{facts.claims.open}{Number(facts.claims.sellerOwes) > 0 ? ` · seller owes ${money(facts.claims.sellerOwes)}` : ''}</td></tr>
          </tbody></Grid>
        </Card>

        {runoff && <Card>
          <CardHead><div><h2>What stands between runoff and closing the book</h2>
            <p>Purchasing has ended. The program closes once each line below is zero.</p></div></CardHead>
          <Grid><tbody>
            <tr><td>Loans still owned</td><td className="num">{closure.openLoans} · {money(closure.openUpb, 0)}</td></tr>
            <tr><td>Purchases not fully paid</td><td className="num">{closure.unpaidAcquisitions}</td></tr>
            <tr><td>Open seller claims</td><td className="num">{closure.openClaims}</td></tr>
            <tr><td>Repurchase cash the seller still owes</td><td className="num">{money(closure.openRepurchaseReceivable)}</td></tr>
            <tr><td>Servicing periods not signed off</td><td className="num">{closure.openServicingPeriods}</td></tr>
            <tr><td>Stops in force</td><td className="num">{closure.activeStopEvents}</td></tr>
          </tbody></Grid>
        </Card>}
      </Work>
    </Layout>

    <DecisionBar aria-label="Next action">
      <div><strong>{nextAction.label}</strong><span>{nextAction.whyNot ?? nextAction.reason}</span></div>
      {nextAction.key !== 'NONE' && nextAction.key !== 'WAIT' && <Button $variant="primary" type="button"
        onClick={() => act(nextAction)}>{nextAction.actorMayAct ? 'Continue' : 'Open'} <ChevronRight size={14} /></Button>}
    </DecisionBar>
  </Shell>;
}
